import type { GameState, StateTransition } from '../types';
import type { ValidAction } from '../../multiplayer/types';
import type {
  CountDestination,
  BossPreservation,
  ContextualControlOutcome,
  PartnerSafetyClassification,
} from './contextual-assessment';

/**
 * Interface for player controllers - both human and AI.
 * Controllers decide when and how to emit actions.
 */
export interface PlayerController {
  readonly playerId: number;

  /**
   * Called whenever game state changes.
   * Controller decides if it needs to take action.
   */
  onStateChange(state: GameState, availableTransitions: StateTransition[]): void;

  /**
   * Cleanup when controller is removed
   */
  destroy?(): void;
}

/**
 * Configuration for a player
 */
export interface PlayerConfig {
  type: 'human' | 'ai';
  name?: string;
  aiStrategy?: 'beginner' | 'intermediate' | 'random';
}

/**
 * AI strategy interface
 */
export interface AIStrategy {
  /**
   * Choose an action from available valid actions
   */
  chooseAction(state: GameState, validActions: ValidAction[]): ValidAction;

  /** Return the action together with the search outcome used to choose it. */
  analyzeAction?(
    state: GameState,
    validActions: ValidAction[],
    options?: { deadlineAt?: number },
  ): AIActionAnalysis;
}

export type AIActionAnalysisStatus = 'forced' | 'scored' | 'fallback';

/** Selection mechanism recorded for a play-decision receipt. */
export type AIActionChoiceMechanism =
  | 'forced'
  | 'highest-score'
  | 'near-equal-sample'
  | 'contextual-preference'
  | 'contextual-sample'
  | 'fallback';

/** Version of the transparent product play policy used for a decision. */
export const AI_PLAY_POLICY_VERSION = 8 as const;
/** Version of the transparent product bid policy used for a decision. */
export const AI_BID_POLICY_VERSION = 1 as const;
/** Version of the transparent product declaration policy used for a decision. */
export const AI_DECLARATION_POLICY_VERSION = 1 as const;
/** Version of the replay-verifiable play receipt shape. */
export const AI_PLAY_RECEIPT_VERSION = 5 as const;
/** Version of the replay-verifiable bid receipt shape. */
export const AI_BID_RECEIPT_VERSION = 1 as const;
/** Version of the replay-verifiable declaration receipt shape. */
export const AI_DECLARATION_RECEIPT_VERSION = 1 as const;

/** Objective used to compare candidate plays. */
export type AIActionDecisionObjective = 'contract-utility' | 'unscored';

/** Why a scored candidate appears before or after another candidate. */
export type AIPlayComparisonOutcome =
  | 'selected'
  | 'contract-utility'
  | Exclude<ContextualControlOutcome, 'equal'>
  | 'stable-order';

/** Deterministic comparison evidence for one scored candidate. */
export interface AIPlayCandidateComparison {
  outcome: AIPlayComparisonOutcome;
  order: number;
}

/** Compact metrics retained for one legal play candidate. */
export interface AIPlayCandidateEvidence {
  /** Semantic engine transition ID (for example, `play-6-2`). */
  actionId: string;
  /** Average team points across evaluated worlds. */
  avgTeamPoints?: number;
  /** Fraction of evaluated worlds that made the contract. */
  winRate?: number;
  /** Contract utility used for selection. */
  contractUtility?: number;
  /** Number of evaluated worlds. */
  simulationCount?: number;
  /** Contextual partner lead assessment across evaluated worlds. */
  partnerSafety?: PartnerSafetyClassification;
  /** Share of evaluated legal continuations won by the partner. */
  partnerWinRate?: number;
  /** Whether this candidate takes a trick currently led by the partner. */
  overtakesPartner?: boolean;
  /** Whether this candidate wins the completed current trick in a strict majority of sampled worlds. */
  candidateWinsTrick?: boolean;
  /** Destination of the current trick's points across legal continuations. */
  countDestination?: CountDestination;
  /** Count value released by this candidate. */
  countRisk?: number;
  /** Whether this domino is the highest unplayed in its suit. */
  liveStanding?: boolean;
  /** Whether this candidate spends or keeps a boss domino. */
  bossPreservation?: BossPreservation;
  /** Whether keeping this domino plausibly creates a useful future lead. */
  likelyFutureLeadValue?: boolean;
  /** Retained count value with a plausible promotion route. */
  protectedCountValue?: number;
  /** Share of retained count supported by a promotion route. */
  promotionSupport?: number;
  /** Share of sampled worlds with a plausible route to the lead. */
  leadAccess?: number;
  /** Share of sampled worlds where the team retains control. */
  remainingControl?: number;
  /** Share of sampled worlds where the retained count plan is cashable. */
  cashability?: number;
  /**
   * Unconditional joint sampled share where a secure count-donation comparison
   * applies and this secure candidate immediately settles the contract.
   */
  contractSecuringRate?: number;
  /** Protected count value released by this play. */
  sacrificedProtectedCountValue?: number;
  /** Whether the acting team retains a trump after this lead. */
  retainedTrumpControl?: boolean;
  /** Whether this lead avoids stripping the partner's final trump. */
  protectsPartnerTrumpControl?: boolean;
  /** Whether this defensive trump lead forces the bidder's final trump. */
  leadPressure?: boolean;
  /** Public aggregate fields for the acting seat's delayed trump plan. */
  actingSeatRetainedTrump?: boolean;
  externalVoidRuffOpportunity?: boolean;
  retainedUsefulContinuation?: boolean;
  weakOffCleanup?: boolean;
  reentryPlanValue?: number;
  /** Deterministic explanation of this candidate's comparison position. */
  comparison?: AIPlayCandidateComparison;
}

export type ProtectedCountDecision =
  | 'none'
  | 'kept-unresolved-count'
  | 'secure-partner-contribution'
  | 'first-safe-count-contribution'
  | 'retained-trump-reentry'
  | 'clear-contract-sacrifice'
  | 'dead-control-release';

/** One leader-versus-alternative confidence comparison. */
export interface AIPairedPlayComparison {
  alternativeActionId: string;
  /** Observed paired contract-utility difference. */
  meanDifference: number;
  /** Existing strategic band that the leader must also clear. */
  strategicTolerance: number;
  /** Mean bounded contribution after the strategic tolerance and world weight. */
  meanCenteredContribution: number;
  /** Standard error of the paired weighted contributions. */
  standardError: number;
  /** Critical value for this family-wise comparison, when identified. */
  criticalValue: number | null;
  /** Paired t statistic, when identified. */
  testStatistic: number | null;
  /** Repeated-look lower bound in weighted contribution units, when identified. */
  lowerBound: number | null;
  separated: boolean;
}

/** Deterministic paired confidence for one ordered hidden-world prefix. */
export interface AIPairedPlayConfidence {
  method: 'paired-t-alpha-spending' | 'exact-paired-enumeration' | 'truncated-enumeration-unavailable';
  /** Statistical level for sampled paired-t evidence; not applicable to enumeration. */
  confidenceLevel: 0.95 | null;
  evaluatedWorldCount: number;
  plannedLookCount: 1 | 4;
  weighting: 'uniform' | 'shared-world-weighted';
  assumption: 'iid-worlds-and-normal-paired-mean' | 'exhaustive-valid-worlds' | 'truncated-world-prefix';
  lowerBoundUnits: 'weighted-contract-utility-contribution';
  informationSet: 'filtered-seat-state-and-legal-actions';
  worldOrder: 'shared-ordered';
  leaderActionId: string;
  clearLeader: boolean;
  /** Empty for a clear leader; otherwise the leader and every unresolved alternative. */
  unresolvedActionIds: string[];
  /** Number of candidates that remain in the unresolved family. */
  unresolvedCandidateCount: number;
  comparisons: AIPairedPlayComparison[];
}

export type AIPlaySearchStopReason =
  | 'clear-leader'
  | 'profile-maximum'
  | 'complete-enumeration'
  | 'truncated-enumeration';

/** Versioned, serializable evidence for one AI play decision. */
export interface AIPlayDecisionEvidence {
  version: typeof AI_PLAY_RECEIPT_VERSION;
  phase: 'playing';
  objective: AIActionDecisionObjective;
  candidates: AIPlayCandidateEvidence[];
  selectedActionId: string;
  choiceMechanism: AIActionChoiceMechanism;
  /** Present for every scored decision with two or more legal plays. */
  pairedConfidence?: AIPairedPlayConfidence;
  /** Present beside paired confidence for every scored multi-candidate search. */
  searchStopReason?: AIPlaySearchStopReason;
  nearEqual?: {
    tolerance: number;
    candidateCount: number;
    preferredCandidateCount?: number;
  };
  futureControlExplanation?: string;
  /** Structured reason for the selected action's protected-count decision. */
  protectedCountDecision?: ProtectedCountDecision;
  protectedCountExplanation?: string;
}

/** Compact metrics retained for one legal bid or pass candidate. */
export interface AIBidCandidateEvidence {
  actionId: string;
  bidType: 'pass' | 'points' | 'marks' | 'splash' | 'plunge';
  bidValue?: number;
  makeBidRate?: number;
  estimatedMatchValue?: number;
  simulationCount?: number;
  scoreGapFromBest?: number;
  pointRaiseMargin?: number;
}

/** Versioned, serializable evidence for one AI bid or pass decision. */
export interface AIBidDecisionEvidence {
  version: typeof AI_BID_RECEIPT_VERSION;
  phase: 'bidding';
  /** Final-seat bids use match value; earlier bids retain the make-rate policy. */
  objective: 'match-value' | 'make-rate' | 'unscored';
  candidates: AIBidCandidateEvidence[];
  selectedActionId: string;
  selectedBidMargin?: number;
  nearEqualActionIds: string[];
}

export interface AIDeclarationCandidateEvidence {
  actionId: string;
  declaration: 'suit' | 'doubles' | 'no-trump' | 'nello' | 'sevens';
  suit?: number;
  order: number;
  score?: number;
  risk?: number;
  control?: number;
}

/** Versioned, serializable evidence for one AI declaration decision. */
export interface AIDeclarationDecisionEvidence {
  version: typeof AI_DECLARATION_RECEIPT_VERSION;
  phase: 'trump_selection';
  objective: 'hand-structure' | 'unscored';
  contractTarget: number;
  candidates: AIDeclarationCandidateEvidence[];
  selectedActionId: string;
  choiceReason:
    | 'highest-score'
    | 'nello-low-hand'
    | 'sevens-concentration'
    | 'fallback';
}

export type AIActionDecisionEvidence =
  | AIPlayDecisionEvidence
  | AIBidDecisionEvidence
  | AIDeclarationDecisionEvidence;

export interface AIActionAnalysis {
  action: ValidAction;
  status: AIActionAnalysisStatus;
  nearEqualCandidateCount?: number;
  evidence?: AIActionDecisionEvidence;
}
